/**
 * Multi-Store Product Renderer
 * Replaces Shopify collection products with unified franchise inventory
 */

class MultiStoreProductRenderer {
  constructor() {
    this.apiBaseUrl = window.inventoryConfig?.apiUrl || 'http://localhost:3002/api/inventory';
    this.enabledStores = window.inventoryConfig?.enabledStores || ['Apple Valley', 'Grand Blanc', 'Mainstream Test Website'];
    this.productsPerPage = 24;
    this.currentPage = 1;
    this.totalProducts = 0;
    
    this.init();
  }

  init() {
    // Check if we should replace products on this page
    if (!this.shouldReplaceProducts()) {
      return;
    }

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.replaceProducts());
    } else {
      this.replaceProducts();
    }
  }

  shouldReplaceProducts() {
    // Only replace on specific collection pages or when config enables it
    return window.inventoryConfig?.replaceCollectionProducts && 
           (window.location.pathname.includes('/collections/') || 
            window.location.pathname.includes('/search'));
  }

  async replaceProducts() {
    try {
      const productGrid = document.querySelector('.product-grid');
      if (!productGrid) return;

      // Show loading state
      this.showLoadingState(productGrid);

      // Fetch products from API
      const products = await this.fetchAllProducts();
      
      // Clear existing products
      productGrid.innerHTML = '';
      
      // Render new products
      this.renderProducts(productGrid, products);
      
      // Update product count
      this.updateProductCount(products.length);
      
    } catch (error) {
      console.error('Failed to replace products:', error);
      this.showErrorState();
    }
  }

  async fetchAllProducts() {
    try {
      const response = await fetch(`${this.apiBaseUrl}/search?available_only=true&limit=${this.productsPerPage * 10}`);
      
      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }
      
      const data = await response.json();
      return data.products || [];
      
    } catch (error) {
      console.warn('API request failed:', error);
      return [];
    }
  }

  renderProducts(container, products) {
    const fragment = document.createDocumentFragment();
    
    products.forEach(product => {
      const productElement = this.createProductElement(product);
      fragment.appendChild(productElement);
    });
    
    container.appendChild(fragment);
  }

  createProductElement(product) {
    // Create product block similar to Shopify's structure
    const productBlock = document.createElement('product-block');
    productBlock.className = 'product-block multi-store-product';
    productBlock.dataset.productId = product.id || product.handle;

    // Get best image
    const imageUrl = product.images && product.images[0] ? 
      this.getImageUrl(product.images[0], '400x400') : 
      this.getPlaceholderImage();

    // Calculate availability
    const availability = this.calculateAvailability(product);
    
    productBlock.innerHTML = `
      <div class="block-inner">
        <div class="block-inner-inner">
          <div class="image-cont">
            <a class="product-link" href="${this.getProductUrl(product)}">
              <div class="image-label-wrap">
                <div>
                  <div class="product-block__image product-block__image--primary product-block__image--active">
                    <img src="${imageUrl}" 
                         alt="${this.escapeHtml(product.title)}"
                         loading="lazy"
                         class="multi-store-product-image">
                  </div>
                </div>
                ${this.renderProductLabels(product, availability)}
              </div>
            </a>
          </div>

          <div class="product-block__detail align-ltr-left">
            <div class="inner">
              <div class="innerer">
                <a class="product-link" href="${this.getProductUrl(product)}">
                  ${product.vendor ? `<div class="vendor">${this.escapeHtml(product.vendor)}</div>` : ''}
                  <div class="product-block__title">${this.escapeHtml(product.title)}</div>
                  ${this.renderPrice(product)}
                </a>
                ${this.renderAvailability(availability)}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    return productBlock;
  }

  calculateAvailability(product) {
    const availableStores = [];
    let totalInventory = 0;
    let bestPrice = null;

    if (product.stores) {
      Object.entries(product.stores).forEach(([storeName, storeData]) => {
        if (this.enabledStores.includes(storeName) && storeData.inventory_quantity > 0) {
          availableStores.push({
            name: storeName,
            quantity: storeData.inventory_quantity,
            price: storeData.price,
            url: storeData.url
          });
          totalInventory += storeData.inventory_quantity;
          
          if (!bestPrice || storeData.price < bestPrice) {
            bestPrice = storeData.price;
          }
        }
      });
    }

    return {
      stores: availableStores,
      totalInventory,
      bestPrice,
      isAvailable: availableStores.length > 0
    };
  }

  renderPrice(product) {
    // Try to get the best price across stores
    let minPrice = null;
    let maxPrice = null;
    
    if (product.stores) {
      Object.values(product.stores).forEach(store => {
        if (store.price) {
          const price = parseFloat(store.price);
          if (!minPrice || price < minPrice) minPrice = price;
          if (!maxPrice || price > maxPrice) maxPrice = price;
        }
      });
    }

    if (minPrice) {
      const priceDisplay = minPrice === maxPrice ? 
        `$${minPrice.toFixed(2)}` : 
        `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;
      
      return `<div class="product-price product-price--block">${priceDisplay}</div>`;
    }
    
    return '<div class="product-price product-price--block">Price varies by location</div>';
  }

  renderAvailability(availability) {
    if (!availability.isAvailable) {
      return `
        <div class="product-inventory-availability">
          <div class="inventory-unavailable">
            <span class="inventory-icon">📦</span>
            <span>Out of stock</span>
          </div>
        </div>
      `;
    }

    if (availability.stores.length === 1) {
      return `
        <div class="product-inventory-availability">
          <div class="inventory-available inventory-single">
            <span class="inventory-icon">✅</span>
            <span>Available at ${availability.stores[0].name}</span>
          </div>
        </div>
      `;
    }

    return `
      <div class="product-inventory-availability">
        <div class="inventory-available inventory-multiple">
          <span class="inventory-icon">✅</span>
          <span>Available at ${availability.stores.length} locations</span>
          <button class="inventory-details-btn" type="button">View stores</button>
        </div>
        <div class="inventory-details-popup" style="display: none;">
          <div class="inventory-popup-content">
            <h4>Available at:</h4>
            ${availability.stores.map(store => 
              `<div class="inventory-store">
                <strong>${store.name}</strong>
                <span class="inventory-qty">${store.quantity > 5 ? '5+' : store.quantity} in stock</span>
                ${store.url ? `<a href="${store.url}" class="store-link">Visit store</a>` : ''}
              </div>`
            ).join('')}
            <button class="inventory-close-btn" type="button">Close</button>
          </div>
        </div>
      </div>
    `;
  }

  renderProductLabels(product, availability) {
    const labels = [];
    
    // Out of stock label
    if (!availability.isAvailable) {
      labels.push('<div class="product-label product-label--sold-out">Sold Out</div>');
    }
    
    // New arrival (if product is recent)
    if (this.isNewArrival(product)) {
      labels.push('<div class="product-label product-label--new">New</div>');
    }

    return labels.length > 0 ? `<div class="product-labels">${labels.join('')}</div>` : '';
  }

  getProductUrl(product) {
    // Try to find a product URL from available stores
    if (product.stores) {
      for (const store of Object.values(product.stores)) {
        if (store.url) {
          return store.url;
        }
      }
    }
    
    // Fallback to search or handle
    return `/search?q=${encodeURIComponent(product.title)}`;
  }

  getImageUrl(image, size = '400x400') {
    if (typeof image === 'string') {
      return image;
    }
    
    if (image.src) {
      // Shopify image transformation
      return image.src.replace(/\.(jpg|jpeg|png|gif|webp)/i, `_${size}.$1`);
    }
    
    return image.url || this.getPlaceholderImage();
  }

  getPlaceholderImage() {
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjNGNEY2Ii8+Cjx0ZXh0IHg9IjIwMCIgeT0iMjAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5Q0EzQUYiIHRleHQtYW5jaG9yPSJtaWRkbGUiPk5vIEltYWdlPC90ZXh0Pgo8L3N2Zz4K';
  }

  isNewArrival(product) {
    // Simple check - could be enhanced with actual date logic
    return product.tags && product.tags.includes('New');
  }

  updateProductCount(count) {
    const countElements = document.querySelectorAll('.collection-count, .results-count');
    countElements.forEach(el => {
      el.textContent = `${count} products`;
    });
  }

  showLoadingState(container) {
    container.innerHTML = `
      <div class="multi-store-loading">
        <div class="loading-spinner"></div>
        <p>Loading products from all locations...</p>
      </div>
    `;
  }

  showErrorState() {
    const productGrid = document.querySelector('.product-grid');
    if (productGrid) {
      productGrid.innerHTML = `
        <div class="multi-store-error">
          <p>Unable to load products. Please try again later.</p>
        </div>
      `;
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize when script loads
if (typeof window !== 'undefined') {
  // Allow configuration via global object
  window.inventoryConfig = window.inventoryConfig || {};
  
  // Initialize the multi-store product renderer
  new MultiStoreProductRenderer();
}